import { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { usePeriodReview } from '@/hooks/usePeriodReview'
import { weekToken } from '@/hooks/usePlanningSession'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  pointerWithin,
  rectIntersection,
  type CollisionDetection,
  type DragMoveEvent,
} from '@dnd-kit/core'
import type { Task } from '@/types/task'
import { PlanWeekMenu } from '@/components/plan/PlanWeekMenu'
import { timeOnDay } from '@/lib/planning/clockText'
import { taskTiming, hasTiming, broaderCommitment, removeDayOutcome, removeAllOutcome } from '@/lib/planning/taskTiming'
import { timingRemoval } from '@/lib/planning/planActions'
import { placementOf } from '@/lib/planning/placement'
import type { CalendarEvent } from '@/hooks/useGoogleCalendar'
import type { Routine, ActionableInstance } from '@/types/actionable'
import { useSupabaseTasks } from '@/hooks/useSupabaseTasks'
import { eventToTimelineItem } from '@/types/timeline'
import { goalTitleMap } from '@/lib/planning/goalSteps'
import { useWeekDragDrop } from './useWeekDragDrop'
import { useFamilyMembers } from '@/hooks/useFamilyMembers'
import { useGatedTaskActions } from '@/hooks/useGatedTaskActions'
import { weekStartAnchor, readCadenceConfig } from '@/lib/cadence/config'
import { resolveRoutineEligible } from '@/lib/routineUtils'
import { partitionWeekExtras } from '@/lib/week/weekExtras'
import { buildWeekRoutineItems } from './weekRoutineItems'
import { useDayChoices } from '@/hooks/useDayChoices'
import { useWeekInstances } from './useWeekInstances'
import { edgeForPointer } from './edgeAdvance'
import { WeekJournal } from './WeekJournal'
import { buildJournalDays, inDayOrder, type JournalDay, type JournalEntry, type JournalWeekend } from '@/lib/week/journalDays'
import { reorderTasksToIndex } from '@/lib/today/taskOrdering'
import { routineMoveKind } from '@/lib/week/routineMove'
import type { DensitySources } from '@/lib/planning/dayDensity'
import { formatWeekRange } from '@/lib/dateHelpers'
import { WeekList } from './WeekList'
import { monthStartOf } from '@/lib/planning/periodPlacement'
import { makePlanActions } from '@/lib/planning/planActions'
import { sameDay } from '@/lib/placement/model'
import type { PlanDragPayload } from '@/lib/planning/planDrag'
import { useActionableInstances } from '@/hooks/useActionableInstances'
import { showToast } from '@/hooks/useToast'
import { eventDays, layoutContextSpans } from '@/lib/week/journalSpread'
import { localYmd } from '@/lib/cadence/config'
import { useSelectionOptional } from '@/shell/providers/SelectionProvider'
import { publishViewedWeek } from '@/lib/viewedWeekSignal'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import type { AssigneeFilter } from '@/lib/today/types'
import { makeAssigneeFilter } from '@/lib/today/assigneeFilter'
import type { Layer } from '@/lib/domains'
import { WeekPlanHost } from './WeekPlanHost'
import { WeekV2 } from '@/components/plan/v2/WeekV2'
import { useWeather, type DayForecast } from '@/hooks/useWeather'
import { useWeekViewPrefs } from '@/hooks/useWeekViewPrefs'
import { planV2Enabled, lineDropUpdates } from '@/lib/planning/v2/planV2'
import { useCanvasActivity } from '@/contexts/CanvasActivityContext'
import { useAssigneeFilter } from '@/hooks/useAssigneeFilter'
import { planPeopleLens } from '@/lib/planning/peopleLens'
import { weekListTasks, weekShownTasks } from '@/lib/planning/weekList'
import { selectPeriodTasks, isUndatedLegacyDone } from '@/lib/planning/periodPage'
import { readSeasons } from '@/lib/cadence/seasons'
import { weekRhythm, rhythmLines } from '@/lib/week/weekRhythm'
import { densityDescription } from '@/lib/planning/dayDensity'
import { dayFreeWindows, formatFree } from '@/lib/week/dayShape'
import { parseSpecials } from '@/lib/today/specials'
import { unhomedRoutines } from '@/lib/week/unhomedRoutines'
import { buildShelf, type ShelfGroup } from '@/components/canvas/week/weekShelf'
import { periodRange } from '@/components/canvas/plan/planGroups'
import { WeekCanvas, type CanvasDay, type CanvasDayItem, type WeekCanvasWriters, type WeekMonthSheet } from '@/components/canvas/week/WeekCanvas'
import { DayWeather } from './WeekJournal'
import { alsoDoneIn, weekReviewState } from '@/lib/planning/periodReview'
import type { WeekDeck } from '@/components/plan/v2/WeekV2'
import { buildDeck, deckModeOf, deckWeekAllowed } from '@/lib/planning/deck'
import { staleForDrain } from '@/lib/planning/drain'
import { withPlacedSubtasks } from '@/lib/planning/plannedSubtasks'
import { useChooseWeek } from './useChooseWeek'
import { restoreDeleted } from '@/lib/planning/restoreDeleted'
import { doableBy } from '@/lib/planning/poolViews'
import { filterTasksForLayers } from '@/lib/today/domainFilter'
import type { StripDay } from '@/components/plan/deck/WeekStrip'
import { useDeckActions } from './useDeckActions'
import { usePlannedDinners } from '@/hooks/usePlannedDinners'
import type { SessionFold } from '@/components/plan/guided/GuidedPeriod'
import { applyTriageWhen, describeTriageWhen } from '@/lib/triage/applyWhen'
import { plannedMealHref, type PlannedMeal } from '@/lib/mealDayRecipes'
import { useCalendarPermissions } from '@/hooks/useCalendarPermissions'

/** Does this calendar event span the whole day? Explicit flags win; otherwise
 *  a full-day span (midnight start, 24h+ duration — how a holiday reads from
 *  Google) counts too. A holiday used to reach the timed grid and get clamped
 *  to the 8 AM row (demo run 2026-09-06) — it belongs in the all-day lane. */
function isAllDayEvent(ev: CalendarEvent): boolean {
  const flagged =
    (ev as { is_all_day?: boolean }).is_all_day ??
    (ev as { isAllDay?: boolean }).isAllDay ??
    ev.all_day ??
    ev.allDay
  if (flagged !== undefined) return flagged
  const startStr = (ev as { start_time?: string }).start_time ?? (ev as { startTime?: string }).startTime
  const endStr = (ev as { end_time?: string }).end_time ?? (ev as { endTime?: string }).endTime
  if (!startStr || !endStr) return false
  const start = new Date(startStr)
  const end = new Date(endStr)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false
  const startsAtMidnight = start.getHours() === 0 && start.getMinutes() === 0
  return startsAtMidnight && end.getTime() - start.getTime() >= 24 * 60 * 60 * 1000
}

interface WeekViewV2Props {
  tasks: Task[]
  events: CalendarEvent[]
  /**
   * How each source stands for the week being VIEWED. The day tiles draw a day
   * as quiet only when they have actually read it: "nothing on Thursday", "we
   * could not read Thursday" and "there is no calendar to read" are three
   * different answers (Codex, 2026-09-24). Omitted, everything reads ready —
   * the shape every existing caller and test already has.
   */
  sources?: DensitySources
  /**
   * The UNFILTERED lists, and the range the events were actually fetched for
   * — for the day tiles' counts only, never for what the journal draws.
   *
   * Density is universal: a day is full regardless of which domain filled it
   * or whose it is. `tasks`/`events` above are what this view DRAWS, narrowed
   * to the reader's layers and assignee, and counting those made /week the
   * one surface that answered "how busy is Tuesday" differently from every
   * other (Codex, 2026-09-25). Omitted, the tiles fall back to the drawn
   * lists and under-count exactly as much as the view is filtered.
   */
  densityTasks?: Task[]
  densityEvents?: CalendarEvent[]
  routines: Routine[]
  // dateInstances is reserved for future instance-completion overlays;
  // not yet consumed in rendering but kept in the API for Task 12 wiring.
  dateInstances: ActionableInstance[]
  weekStart: Date
  onWeekChange: (d: Date) => void
  selectedAssignee?: string | null
  /** Multi-select assignee filter (rung 5). Superset of `selectedAssignee`;
   *  when provided it drives resolveRoutine directly. */
  selectedAssignees?: AssigneeFilter
  /** The checked layers (rung 4). Unsorted is a layer, not a wildcard. */
  layers: ReadonlySet<Layer>
  onSelectItem: (id: string | null) => void
  onUpdateTask: (taskId: string, updates: Partial<Task>) => Promise<void | boolean> | void
  /** Move an event. Absent = this host cannot move events, and the grid says
   *  so rather than announcing a move it did not make. */
  onUpdateEvent?: (eventId: string, updates: { startTime: Date; endTime: Date }) => Promise<void> | void
  onUpdateRoutine: (routineId: string, updates: Partial<Routine>) => Promise<void> | void
  /** Pin a routine to a time on ONE day (override write, recurrence rule
   *  untouched). Present = routine blocks become draggable. */
  onPushRoutine?: (routineId: string, when: Date, fromDate: Date) => void
  /** Number of day columns drawn from `weekStart` (1–7). 7 = the week; a
   *  preset or custom range from the masthead draws fewer. A range is a VIEW —
   *  the list beside the grid stays this week's plan, and any other week the
   *  range touches folds beneath it. Default 7. */
  dayCount?: number
  /** From HomeView's useUndo. Called after successful mutations to surface an undo toast. */
  pushAction?: (message: string, undo: () => void) => void
  /** The page's own optimistic order writer (the container's
   *  `updateTaskOrders`, `ctx.onReorderTasks` — what Today uses): the drop
   *  shows at once, and the next drag plans from it. A null order puts a row
   *  back to never-ordered (Undo). */
  onReorderTasks?: (writes: { id: string; sortOrder: number | null }[]) => Promise<boolean>
}

const weekIsCurrentFor = (anchor: Date) => sameDay(anchor, weekStartAnchor(new Date(), readCadenceConfig().weekStartsOn))

export function WeekViewV2(props: WeekViewV2Props) {
  const {
    tasks: listedTasks,
    events,
    sources,
    routines,
    weekStart,
    onWeekChange,
    selectedAssignees,
    layers,
    onSelectItem,
    onUpdateTask,
    onUpdateEvent,
    onUpdateRoutine,
    onPushRoutine,
    dayCount = 7,
    densityTasks,
    densityEvents,
    pushAction,
    onReorderTasks,
  } = props

  // A step planned for a week or given a day is week work, even when it is
  // a subtask still nested under its line (spec 2026-10-19).
  const tasks = useMemo(() => withPlacedSubtasks(listedTasks), [listedTasks])

  // Create-gesture wiring
  const navigate = useNavigate()
  const { addTask, deleteTask, toggleTask, updateTask, updateTasksBulk, updateTaskOrders, pushTask, userId, keepForward } = useSupabaseTasks()
  // Orders go through the rendered list's own writer, so a drop moves the row
  // at once; this page's hook instance is only the fallback (its optimistic
  // state is not what the page draws).
  const writeOrders = onReorderTasks ?? updateTaskOrders
  // The rail plans MY week — scope it to the current member, as the strip does.
  const { getCurrentUserMember, members: familyMembers } = useFamilyMembers()
  const meId = getCurrentUserMember()?.id ?? null

  // Pool-pill triage. Defers run through the DomainGate (a context-less task
  // asks "Where does this belong?" before the write — Iris's rule); "not this
  // week" rides the already-gated onUpdateTask prop with the same write the
  // Plan-Your-Time overlay makes.
  const gated = useGatedTaskActions(
    { updateTask, pushTask, updateTasksBulk },
    (id) => tasks.find((t) => t.id === id),
  )
  // Tell the shell which week is on screen, so the Today pin's week list
  // answers the same question this page is asking. Cleared on unmount — the
  // pin falls back to the real current week beside every other page.
  // The prop is a RANGE start (Saturday for ?range=weekend, Monday for the
  // workweek); the list and the pin key on the week ANCHOR, like belongsToWeek.
  const weekAnchor = useMemo(() => weekStartAnchor(weekStart, readCadenceConfig().weekStartsOn), [weekStart])
  useEffect(() => { publishViewedWeek(weekAnchor) }, [weekAnchor])
  // Only unmount clears it — a cleanup on every change published null → week
  // and defeated the signal's same-week dedupe.
  useEffect(() => () => publishViewedWeek(null), [])

  // Drag-drop wiring. Domain-on-drop needs nothing here: onUpdateTask is the
  // GATED update (ctx.onUpdateTask = useGatedTaskActions), so scheduling a
  // context-less task opens the DomainGate modal before the write — Iris's
  // rule, enforced in one place.
  const drag = useWeekDragDrop({
    weekStart,
    onWeekChange,
    onUpdateTask,
    onUpdateEvent,
    onUpdateRoutine,
    tasks,
    events,
    routines,
    dayCount,
    pushAction,
    onPushRoutine,
    // One drag rule (2026-10-03): read at drop time, after the page's own
    // writers below exist.
    onTakeIn: (taskId) => takeIntoWeek(taskId),
    onRoutineToDay: (routineId, fromIso, toIso, title) => { void moveRoutineToDay(routineId, fromIso, toIso, title) },
  })

  // Sensor with activation constraint — disambiguates click vs drag.
  // Drag activates only after pointer moves 8px from origin; below that,
  // onClick/onPointerUp fire normally on the block.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  )

  // The drop lands where the CURSOR is, not where the dragged element's box
  // happens to overlap. dnd-kit's default (rectIntersection) scores droppables
  // by area, and a list pill is 262x60 — wide enough to cover whole 15-minute
  // sub-slots in two day columns at once, so the winner was decided by the
  // pill's geometry and could sit a day off from where you aimed. pointerWithin
  // asks the one question a person is asking ("which cell is under my
  // pointer?"); rectIntersection stays as the fallback for the moments the
  // pointer is over no droppable at all (between the grid's cells, over a
  // block), so a drop is never silently swallowed.
  const collisionDetection = useCallback<CollisionDetection>((args) => {
    const byPointer = pointerWithin(args)
    return byPointer.length > 0 ? byPointer : rectIntersection(args)
  }, [])

  // Edge-hover state for cross-week auto-advance, measured against the grid box.
  const [edgeHover, setEdgeHover] = useState<'left' | 'right' | null>(null)
  const gridBoundsRef = useRef<HTMLDivElement>(null)
  // Has this drag been on the grid yet? A pill dragged out of the list column
  // starts LEFT of the grid; without this, it armed the back-a-week timer at
  // pointer-down and the week flipped out from under the drop (edgeAdvance.ts).
  const enteredGridRef = useRef(false)

  const handleDragMove = (e: DragMoveEvent) => {
    // activatorEvent is the pointer-down that started the drag; adding delta.x
    // gives the current pointer x position relative to the page.
    const activator = e.activatorEvent as PointerEvent | undefined
    const rect = gridBoundsRef.current?.getBoundingClientRect()
    if (!rect) return

    const currentX = (activator?.clientX ?? 0) + (e.delta?.x ?? 0)
    const { edge, entered } = edgeForPointer(currentX, rect, enteredGridRef.current)
    enteredGridRef.current = entered

    if (edge !== edgeHover) {
      setEdgeHover(edge)
      drag.notifyEdge(edge)
    }
  }

  // Every drag starts fresh: the grid has not been entered until this one
  // enters it.
  const handleDragStart = (e: Parameters<typeof drag.dndHandlers.onDragStart>[0]) => {
    enteredGridRef.current = false
    setEdgeHover(null)
    drag.dndHandlers.onDragStart(e)
  }

  // Week bounds: [weekStart, weekStart + dayCount days)
  const weekEnd = useMemo(() => {
    const e = new Date(weekStart)
    e.setDate(e.getDate() + dayCount)
    return e
  }, [weekStart, dayCount])

  const inWeek = (d: Date) => d >= weekStart && d < weekEnd

  // What the days draw, narrowed by the people filter (the top bar's, shared
  // with Today). `tasks` stays whole for lookups — a drag, a goal's title.
  const drawnTasks = useMemo(() => {
    const match = makeAssigneeFilter(selectedAssignees)
    return tasks.filter((t) => match(t.assignedTo, t.assignedToAll))
  }, [tasks, selectedAssignees])

  // In-week timed calendar events. A holiday (all-day) stays out of this set —
  // isAllDayEvent keeps it from being clamped onto the 8 AM row (demo run
  // 2026-09-06); the days draw it from `events` directly.
  const weekEvents = useMemo(() => events.filter((ev) => {
    const startStr =
      (ev as { start_time?: string }).start_time ??
      (ev as { startTime?: string }).startTime
    if (!startStr) return false
    return inWeek(new Date(startStr)) && !isAllDayEvent(ev)
  }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [events, weekStart])


  const narrow = useMediaQuery('(max-width: 1023px)')

  // Instance-level overrides for the whole visible week — what a drag on this
  // grid writes. The container's `dateInstances` covers one column only.
  const weekInstances = useWeekInstances(weekStart, dayCount)

  // Convert to TimelineItems for grid rendering. Routine expansion (one item
  // per day column, with any instance-level override applied) lives in
  // buildWeekRoutineItems.
  // Dinner + Specials events leave the time grid (This Week redesign):
  // dinners render in the grid's dinner row, specials fold into the same
  // day's School block subtitle (or stay grid material without one).
  const extras = useMemo(() => partitionWeekExtras(weekEvents), [weekEvents])

  // From the FULL task list, not the week's subset: a step placed on this week
  // whose goal sits on the month list would otherwise lose its label.
  const goalTitles = useMemo(() => goalTitleMap(tasks), [tasks])
  const labelFor = useCallback(
    (t: Task) => (t.goalTaskId ? goalTitles.get(t.goalTaskId) : undefined),
    [goalTitles],
  )

  // Shared by the grid and the journal so the two modes can never disagree
  // about them (weekEventItems).
  const eventItems = useMemo(() => weekEventItems(extras), [extras])

  // Routines are always on the week, a day's untimed ones folded behind one
  // line (Scott, 2026-10-03). The toolbar's Routines switch that hid them all
  // is retired here; a routine is hidden one at a time ("Show in Today and
  // planning"), and Today keeps its own "hide daily" choice.
  const routineItems = useMemo(
    () => buildWeekRoutineItems({
      routines,
      weekStart,
      dayCount,
      instances: weekInstances,
      member: selectedAssignees,
      prefs: { hideRoutines: false, layers },
    }),
    [routines, weekStart, dayCount, weekInstances, selectedAssignees, layers],
  )

  // ── Journal spread ────────────────────────────────────────────────────
  // The same inputs the grid reads (layer-filtered tasks and events, routines
  // through the one resolver ladder with its instance overrides), laid out as
  // days instead of hours. Unlike the grid it keeps the whole day: tasks too
  // early for the grid's first hour, all-day tasks, all-day events, and what
  // has been done (struck, the way a paper week keeps it).
  // Each day ahead wears its forecast (Scott, 2026-09-30).
  const { weather } = useWeather()
  const forecast = useMemo(
    () => Object.fromEntries((weather?.dailyForecast ?? []).map((d) => [d.date, d])),
    [weather],
  )
  // Tasks planned for a weekend with no day of their own: "Sometime this
  // weekend" holds them (buildJournalDays).
  // The people filter applies here as on the days (final review, 2026-10-03).
  const weekendTasks = useMemo(() => drawnTasks.filter((t) => t.weekendStart && !t.scheduledFor && !t.completed), [drawnTasks])
  // Each day's planned dinner (Scott, 2026-10-14): the meal plan the kitchen
  // wall reads, in the day's dinner row where no "Dinner:" event is there.
  const plannedDinners = usePlannedDinners(weekStart, dayCount)
  const journal = useMemo(() => buildJournalDays({
    weekStart, dayCount, tasks: drawnTasks, weekendTasks, userId, eventItems, events,
    dinnersByDay: extras.dinnersByDay, routineItems, instances: weekInstances, labelFor,
  }), [drawnTasks, weekendTasks, userId, events, eventItems, extras, routineItems, weekInstances, weekStart, dayCount, labelFor])
  const journalDays = journal.days
  const journalWeekend = journal.weekend

  /**
   * How much is already on each day of the week being VIEWED, for the timing
   * control's day tiles. Counted off `journalDays` — the list the page itself
   * draws — rather than re-derived, so the tiles can never disagree with the
   * days beneath them. Entries are already one-per-thing there; all-day notes
   * are events too, and a dinner is the day's meal, not a commitment to plan
   * around, so it is left out.
   */
  /**
   * The same hook every other surface uses, given /week's OWN calendar read.
   *
   * The counting rules moved out of this component in the previous batch; the
   * SOURCES follow them now. Two weeks around the week on screen reaches
   * further than the shared planning window, so a week paged into December is
   * still counted — and because the lists handed over are unfiltered, the
   * answer matches Today's and the planning pages' for the same day.
   *
   * The journal above is untouched: what it draws is still the reader's own
   * filtered view. Only the tiles' counts changed, and they say their scope.
   */
  const dayChoiceSource = useDayChoices({
    windowStart: weekStart,
    dayCount,
    tasks: densityTasks ?? tasks,
    userId: userId ?? null,
    routines,
    instances: weekInstances,
    calendar: {
      events: densityEvents ?? events,
      // `sources.events` already answers "does what we hold cover the week on
      // screen" — `densitySourcesFor` is given that exact range.
      status: sources?.events ?? 'ready',
    },
  })

  const journalSpans = useMemo(
    () => layoutContextSpans(events, journalDays.map((d) => d.date)),
    [events, journalDays],
  )

  // Ticking from the page. A task's undo writes the EXPLICIT prior state (a
  // second toggle would read a stale snapshot — HomeView's rule). A routine
  // ticks its OCCURRENCE — that day's instance — so Today, the pin and this
  // page show the same completion.
  const { markDone, undoDone, setPlanned, reschedule: rescheduleInstance, undoReschedule } = useActionableInstances()
  const handleJournalToggle = useCallback((entry: JournalEntry, day: JournalDay) => {
    if (entry.task) {
      const task = entry.task
      const was = task.completed
      void toggleTask(task.id).then((ok) => {
        if (!ok) return // rolled back and reported by the write itself
        pushAction?.(was ? 'Task marked incomplete' : 'Task completed', () => {
          void onUpdateTask(task.id, { completed: was })
        })
      })
      return
    }
    if (entry.routineId) {
      const id = entry.routineId
      const done = !entry.completed
      void (done ? markDone('routine', id, day.date) : undoDone('routine', id, day.date))
      pushAction?.(done ? 'Routine completed' : 'Routine marked incomplete', () => {
        void (done ? undoDone('routine', id, day.date) : markDone('routine', id, day.date))
      })
    }
  }, [toggleTask, onUpdateTask, pushAction, markDone, undoDone])

  // Rows dragged out of the Today pin (native drag): a day chooses the day, a
  // slot gives the time. Same writes as the pin's own buttons (planActions).
  const planActions = useMemo(() => makePlanActions({
    findTask: (id) => tasks.find((t) => t.id === id) ?? tasks.flatMap((t) => t.subtasks ?? []).find((t) => t.id === id),
    updateTask: (id, u) => onUpdateTask(id, u),
    pushTask: (id, target) => gated.pushTask(id, target),
    setRoutinePlanned: (id, day, planned) => setPlanned('routine', id, day, planned),
    rescheduleRoutine: (id, from, when) => rescheduleInstance('routine', id, from, when),
    pushAction,
    notify: (m) => showToast(m, 'warning'),
  }), [tasks, onUpdateTask, gated, setPlanned, rescheduleInstance, pushAction])
  // A month line dragged onto the week's list: the same write as its
  // "+ This week" (it stays on the month's plan too).
  const takeIntoWeek = useCallback((taskId: string) => {
    const task = tasks.find((t) => t.id === taskId)
    if (!task) return
    const prev = { bucket: task.bucket, weekStart: task.weekStart as Date }
    void onUpdateTask(taskId, { bucket: 'week', weekStart: weekAnchor })
    pushAction?.(`“${task.title}” → this week`, () => { void onUpdateTask(taskId, prev) })
  }, [tasks, onUpdateTask, weekAnchor, pushAction])
  // A routine occurrence dragged to another day moves as THAT occurrence; a
  // "Sometime this weekend" routine dragged onto Saturday or Sunday is given
  // that day (planned there). The rule itself is never rewritten by a drag.
  // `report` false: the caller reports (the week canvas, through the activity
  // strip) and gets the way back instead of a second Undo toast.
  const moveRoutineToDay = useCallback(async (routineId: string, fromIso: string, toIso: string, title: string, report = true): Promise<{ ok: boolean; undo?: () => Promise<unknown> }> => {
    const [ty, tm, td] = toIso.split('-').map(Number)
    const toDay = new Date(ty, tm - 1, td)
    const rule = routines.find((r) => r.id === routineId)
    const weekendKeys = journalWeekend ? [journalWeekend.satIndex, journalWeekend.sunIndex].flatMap((i) => (i === null ? [] : [journalDays[i].key])) : []
    const [fy, fm, fd] = fromIso.split('-').map(Number)
    const fromDay = new Date(fy, fm - 1, fd)
    const kind = routineMoveKind({ patternType: rule?.recurrence_pattern.type, fromIso, toIso, weekendKeys, inSometime: !!journalWeekend?.sometime.some((e) => e.routineId === routineId) })
    const moved = `Moved "${title}" to ${toDay.toLocaleDateString('en-US', { weekday: 'long' })}`
    if (kind === 'plan') {
      if (report) return { ok: !!(await planActions.chooseRoutine(routineId, toDay, true, title)) }
      const ok = !!(await setPlanned('routine', routineId, toDay, true))
      return { ok, undo: () => setPlanned('routine', routineId, toDay, false) }
    }
    if (kind === 'replan') {
      // Off the old weekend day, onto the new one; one Undo puts it back.
      const ok = (await setPlanned('routine', routineId, fromDay, false)) && (await setPlanned('routine', routineId, toDay, true))
      const undo = () => setPlanned('routine', routineId, toDay, false).then(() => setPlanned('routine', routineId, fromDay, true))
      if (ok && report) pushAction?.(moved, () => { void undo() })
      return { ok: !!ok, undo }
    }
    const [hh, mm] = (rule?.time_of_day ?? '').split(':').map(Number)
    const timed = Number.isFinite(hh)
    const when = timed ? new Date(ty, tm - 1, td, hh, mm || 0) : toDay
    const previous = await rescheduleInstance('routine', routineId, fromDay, when, timed ? undefined : { dayOnly: true })
    if (previous && report) pushAction?.(moved, () => { void undoReschedule(previous) })
    return { ok: !!previous, undo: previous ? () => undoReschedule(previous) : undefined }
  }, [journalWeekend, journalDays, planActions, routines, setPlanned, rescheduleInstance, undoReschedule, pushAction])

  // "+ Add" on a journal day: a dated, all-day task on that day, assigned to
  // me. Captures never inherit the view's lens, so it lands Unsorted — and
  // when the current filter would then hide it, the toast says so rather
  // than letting the row vanish (the walkthrough's silent-filter trap).
  const handleAddToDay = useCallback(async (day: JournalDay, title: string, time?: string) => {
    // With a time ("Can't move": an appointment), at that time; else any time that day.
    const [h, m] = time ? time.split(':').map(Number) : [NaN, NaN]
    const at = Number.isFinite(h) ? new Date(day.date.getFullYear(), day.date.getMonth(), day.date.getDate(), h, m || 0) : day.date
    const id = await addTask(title, undefined, undefined, at, { isAllDay: !Number.isFinite(h), assignedTo: meId ?? undefined })
    if (!id) return
    const when = `${day.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}${Number.isFinite(h) ? `, ${at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}` : ''}`
    const hidden = !layers.has('unsorted')
    showToast(`Added to ${when} · Unsorted · only you${hidden ? ' · hidden by your current view' : ''}`, hidden ? 'warning' : 'success', hidden ? 8000 : undefined)
    pushAction?.(`Added "${title}"`, () => { void deleteTask(id) })
  }, [addTask, deleteTask, meId, layers, pushAction])

  const handlePlanDropOnDay = useCallback((day: JournalDay, payload: PlanDragPayload) => {
    void planActions.drop(payload, { type: 'day', day: day.date })
  }, [planActions])
  // WeekEventBlock.onSelect expects (id: string), but onSelectItem is
  // (id: string | null). Narrow here so TypeScript is satisfied; passing null
  // is only needed for deselection, which happens elsewhere.
  // Routine items get a synthetic '-dayN' suffix for React key uniqueness;
  // strip it before forwarding so the detail panel's id-resolution matches the
  // actual routine id stored in the DB.
  // The day grid uses it too: a routine clicked in a day or in "Sometime this
  // weekend" opened nothing (Scott, 2026-10-04).
  const selectionCtx = useSelectionOptional()
  const handleSelectBlock = (id: string) => {
    if (id.startsWith('routine-')) {
      // Open the routine on the day it was tapped, so its panel offers that
      // occurrence ("Thu Oct 15 only": I'll do it, Skip it, Another day).
      const m = /^routine-(.+)-day(\d+)$/.exec(id)
      if (m && selectionCtx) {
        const day = new Date(weekStart)
        day.setDate(day.getDate() + Number(m[2]))
        selectionCtx.setSelection({ kind: 'routine', id: m[1], date: localYmd(day) })
        return
      }
      onSelectItem(id.replace(/-day\d+$/, ''))
      return
    }
    onSelectItem(id)
  }

  // Keyboard nav: '[' previous week, ']' next week. Skips when focus is in
  // an input/textarea/contentEditable so it doesn't hijack typing.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (e.key === '[') {
        const next = new Date(weekStart)
        next.setDate(next.getDate() - dayCount)
        onWeekChange(next)
      } else if (e.key === ']') {
        const next = new Date(weekStart)
        next.setDate(next.getDate() + dayCount)
        onWeekChange(next)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [weekStart, dayCount, onWeekChange])

  // The week's planning lives in the ONE Planning panel (Scott, 2026-09-21):
  // the dock beside the page on desktop, a sheet on a phone. The viewport is
  // the days. When the panel is closed the page offers it in one line, so a
  // week is never a wall of days with no way to fill them.
  const weekIsCurrent = sameDay(weekAnchor, weekStartAnchor(new Date(), readCadenceConfig().weekStartsOn))
  /**
   * The timing control on a week row — the same component the month and season
   * pages use, so the week is an execution view of the same work rather than a
   * different vocabulary (requirement 2).
   *
   * The weeks it offers come from the week being VIEWED, never the week
   * containing now. "Keep it in <month>" is offered only when the task has a
   * broader commitment to fall back to: with nothing above it, clearing the
   * week has no destination to name, and the brief forbids promising one.
   */
  /** The week's own days as tiles, with what each already holds. */
  const dayChoices = useMemo(
    () => dayChoiceSource.forWeek(weekStart, dayCount),
    [dayChoiceSource, weekStart, dayCount],
  )

  const weekTimingControl = useCallback((task: Task) => {
    const t = taskTiming(task)
    // What actually survives a removal, read from commitments — not from the
    // cached monthStart, which outlives a commitment that was removed, and
    // never from the goal link, which is not a period commitment at all.
    const broader = broaderCommitment(task)
    const removeTiming = (scope: 'day' | 'all') => {
      const { updates, previous } = timingRemoval(task, scope)
      const kept = scope === 'day' ? removeDayOutcome(t, broader?.label ?? null) : removeAllOutcome(t, broader?.label ?? null)
      const what = scope === 'day'
        ? `Removed ${t.day!.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} from “${task.title}”.`
        : `Removed ${t.day ? 'the day and the week' : 'the week'} from “${task.title}”.`
      void Promise.resolve(onUpdateTask(task.id, updates)).then((ok) => {
        if (ok === false) return
        showToast(`${what} ${kept}`, 'success', 8000, {
          label: 'Undo', onClick: () => { void onUpdateTask(task.id, previous) },
        })
      })
    }
    return (
      <PlanWeekMenu
        size="sm"
        title={task.title}
        periodStart={weekAnchor}
        periodLabel={broader?.label ?? undefined}
        timing={t}
        currentWeekStart={t.week}
        // The days of the week in VIEW, never today's: choosing from a
        // November row must offer November days (Scott, 2026-09-24).
        dayChoices={dayChoices}
        dayChoicesLabel={`A day in ${formatWeekRange(weekAnchor)}`}
        onPickWeek={(weekStart) => { void onUpdateTask(task.id, { bucket: 'week', weekStart, scheduledFor: undefined }) }}
        onClearWeek={hasTiming(t) ? () => removeTiming('all') : undefined}
        onRemoveDay={t.day ? () => removeTiming('day') : undefined}
        onPickTime={t.day ? (time) => { void onUpdateTask(task.id, timeOnDay(t.day!, time)) } : undefined}
        onPickDay={(date) => {
          void (async () => {
            if ((await onUpdateTask(task.id, { bucket: 'timed', scheduledFor: date, isAllDay: true })) === false) return
            // Where it went and the way there; no time is implied (horizon flows).
            const isToday = localYmd(date) === localYmd(new Date())
            showToast(`Planned “${task.title}” for ${isToday ? 'today' : date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} — any time that day. It stays on this week.`, 'success', 6000, {
              label: isToday ? 'Open Today' : 'Open day',
              onClick: () => navigate(isToday ? '/today' : `/today?date=${localYmd(date)}`),
            })
          })()
        }}
      />
    )
  }, [weekAnchor, onUpdateTask, dayChoices, navigate])

  /** The month's open goals, for the week's add box: the month this week
   *  mostly lives in, as the week session reads it. */
  const weekGoals = useMemo(() => {
    const month = monthStartOf(new Date(weekAnchor.getTime() + 3 * 86_400_000))
    return {
      label: month.toLocaleDateString('en-US', { month: 'long' }),
      goals: tasks
        .filter((t) => t.isGoal && !t.completed && t.bucket === 'month' && t.monthStart && localYmd(t.monthStart) === localYmd(month))
        .map((t) => ({ id: t.id, title: t.title })),
    }
  }, [tasks, weekAnchor])

  const weekListFor = () => (
    <WeekList
      key={localYmd(weekAnchor)}
      tasks={tasks}
      weekStart={weekAnchor}
      meId={meId}
      userId={userId}
      isCurrent={weekIsCurrent}
      peopleFiltered={Array.isArray(selectedAssignees) ? selectedAssignees.length > 0 : !!selectedAssignees}
      onToggle={(task) => handleJournalToggle({ id: `task-${task.id}`, kind: 'task', title: task.title, completed: task.completed, task }, journalDays[0])}
      onSelect={(id) => onSelectItem(`task-${id}`)}
      goals={weekGoals.goals}
      goalsLabel={weekGoals.label}
      onAdd={async (title, goalId) => {
        // Toward a goal: its next action, born in the goal's life area (the
        // rule the month page's next-action box follows). The goal stays on
        // its month; only the action is on the week.
        const goal = goalId ? tasks.find((t) => t.id === goalId) : undefined
        const id = await addTask(title, undefined, undefined, undefined, { bucket: 'week', weekStart: weekAnchor, assignedTo: meId ?? undefined, goalTaskId: goal?.id, context: goal?.context ?? undefined })
        if (!id) throw new Error('Task creation failed')
        const hidden = goal?.context ? !layers.has(goal.context) : !layers.has('unsorted')
        // ONE notification per add (S3-12): the Undo carries what the old
        // second toast said. A row the current view hides still warns on its
        // own, because that is news the Undo line does not give.
        const where = goal ? `to the week · toward “${goal.title}”` : 'to the week · Unsorted · only you'
        if (pushAction) pushAction(`Added "${title}" ${where}`, () => { void deleteTask(id) })
        else showToast(`Added ${where}`, 'success')
        if (hidden) showToast('Hidden by your current view — turn on Unsorted to see it', 'warning', 8000)
      }}
      timingControl={weekTimingControl}
    />
  )

  // ── The week canvas (Days; approved composition 2026-10-10) ───────────
  // The same data the journal and the week's list read: the week's list
  // through the top bar's people lens, flat; the days as buildJournalDays
  // assembles them; the month folded beside them, for reading; and the
  // weekly routines that still need a day (unhomedRoutines). Every write goes
  // through the canvas activity strip with an Undo that restores what it
  // changed.
  const activity = useCanvasActivity()
  const [people] = useAssigneeFilter()
  const lens = useMemo(() => planPeopleLens(people, meId), [people, meId])
  const weekListed = useMemo(() => weekListTasks(tasks, weekAnchor, lens.scopeId, { isCurrent: weekIsCurrentFor(weekAnchor) }).filter(lens.keep), [tasks, weekAnchor, lens])
  // The week's count, and the guided session's "{n} of about 15": the tasks
  // the page draws for this week (its list and its days), through exactly
  // their filters.
  const weekShown = useMemo(() => weekShownTasks({ listed: weekListed, drawn: drawnTasks, weekStart: weekAnchor }), [weekListed, drawnTasks, weekAnchor])
  const shelf = useMemo(() => weekShelf(weekAnchor, weekListed), [weekAnchor, weekListed])
  const routinesToPlace = useMemo(() => unhomedRoutines(routines, { member: selectedAssignees, prefs: { hideRoutines: false, layers } }, { weekStart: weekAnchor, instances: weekInstances })
    .map((r) => ({ id: r.id, title: r.name })), [routines, selectedAssignees, layers, weekAnchor, weekInstances])
  // Every-day routines: written once above the days (default), or in each
  // day — the page's remembered choice (useWeekViewPrefs, 'symphony-week-daily',
  // set from the header's ⋯ menu).
  const { dailyInDays: dailyShown } = useWeekViewPrefs()
  // A timed event drags to another day when this page can write events and
  // its calendar is not view-only (the event's calendar, from the events read).
  const { isReadOnlyCalendar } = useCalendarPermissions()
  const eventCalendar = useMemo(() => new Map(events.map((ev) => [`event-${ev.google_event_id || ev.id}`, ev.calendar_id ?? ev.calendarId ?? null])), [events])
  const canMoveEvent = useCallback((id: string) => !!onUpdateEvent && eventCalendar.has(id) && !isReadOnlyCalendar(eventCalendar.get(id)),
    [onUpdateEvent, eventCalendar, isReadOnlyCalendar])
  const canvasDays = useMemo(() => toCanvasDays({
    days: journalDays, weekend: journalWeekend, shelf, dailyShown, forecast, plannedDinners, eventsReady: sources?.events === 'ready', canMoveEvent,
  }), [journalDays, journalWeekend, shelf, dailyShown, forecast, plannedDinners, sources?.events, canMoveEvent])
  // Beta: drafts stay in chat. The week always shows the stored plan;
  // successful writes reach it through the shared task hooks.

  const layerTasks = useMemo(() => filterTasksForLayers(tasks, layers), [tasks, layers])
  const planMonth = useMemo(() => monthStartOf(new Date(weekAnchor.getTime() + 3 * 86_400_000)), [weekAnchor])
  const planLines = useMemo(() => selectPeriodTasks(layerTasks.filter(lens.keep), 'month', planMonth,
    planMonth.getTime() === monthStartOf(new Date()).getTime(), lens.scopeId, readSeasons()), [layerTasks, lens, planMonth])
  // The month, folded beside the days for reading (paper-style horizons,
  // 2026-11-04): its open lines, titles only, through the same filters.
  const monthSheet = useMemo<WeekMonthSheet>(() => {
    const monthEnd = new Date(planMonth.getFullYear(), planMonth.getMonth() + 1, 1)
    return {
      word: planMonth.toLocaleDateString('en-US', { month: 'long' }),
      kicker: `Month · ${periodRange(planMonth, monthEnd)}`,
      rows: planLines.filter((t) => !t.completed).map((t) => ({ id: t.id, title: t.title })),
      doneCount: planLines.filter((t) => t.completed).length,
    }
  }, [planLines, planMonth])
  // A guided session listening for the canvas's saved writes (WeekV2's
  // session marks the week planned when one lands on its choosing step).
  const canvasWriteListener = useRef<(() => void) | null>(null)
  const listenCanvasWrites = useCallback((listener: () => void) => {
    canvasWriteListener.current = listener
    return () => { if (canvasWriteListener.current === listener) canvasWriteListener.current = null }
  }, [])
  const canvasWriters = useMemo<WeekCanvasWriters>(() => {
    const short = (d: Date) => d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    // A placement (a task or routine given a day or a when, an add) goes
    // through here: the activity strip's receipt and Undo, then — when it
    // saved — word to a listening session. Ticking done, taking a day off,
    // reordering, deleting and moving an event are not choosing, and run
    // through the activity alone.
    const run: typeof activity.run = async (label, command, options) => {
      const ok = await activity.run(label, command, options)
      if (ok) canvasWriteListener.current?.()
      return ok
    }
    const write = (label: string, t: Task, patch: Partial<Task>, previous?: Partial<Task>, placing = true) => {
      // The way back is exactly what the write changed.
      const before = previous ?? Object.fromEntries(Object.keys(patch).map((k) => [k, t[k as keyof Task]])) as Partial<Task>
      void (placing ? run : activity.run)(label, async () => (await onUpdateTask(t.id, patch)) as boolean | void, {
        ids: [t.id], undo: async () => { await onUpdateTask(t.id, before) },
      })
    }
    const atMidnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate())
    return {
      placeTask: (t, day) => write(`Give “${t.title}” ${short(day)}`, t, lineDropUpdates(t, { kind: 'day', at: atMidnight(day) })),
      moveTask: (t, day) => {
        const timed = !!t.scheduledFor && t.isAllDay === false
        if (!timed) { write(`Move “${t.title}” to ${short(day)}`, t, lineDropUpdates(t, { kind: 'day', at: atMidnight(day) })); return }
        const at = new Date(day.getFullYear(), day.getMonth(), day.getDate(), t.scheduledFor!.getHours(), t.scheduledFor!.getMinutes())
        // A timed task keeps its time on the new day.
        write(`Move “${t.title}” to ${short(day)}`, t, { scheduledFor: at, isAllDay: false, bucket: 'timed' })
      },
      unplaceTask: (t) => { const { updates, previous } = timingRemoval(t, 'day'); write(`Back to still to place: “${t.title}”`, t, updates, previous, false) },
      toggleTask: (t) => {
        const was = t.completed
        void activity.run(was ? `Reopen “${t.title}”` : `Done: “${t.title}”`, () => toggleTask(t.id), {
          ids: [t.id], undo: async () => { await onUpdateTask(t.id, { completed: was }) },
        })
      },
      toggleRoutine: (it, day) => {
        if (!it.routineId) return
        const id = it.routineId
        const done = !it.completed
        void activity.run(done ? `Done: “${it.title}”` : `Reopen “${it.title}”`,
          async () => { await (done ? markDone('routine', id, day) : undoDone('routine', id, day)) },
          { ids: [id], undo: async () => { await (done ? undoDone('routine', id, day) : markDone('routine', id, day)) } })
      },
      placeRoutine: (r, day) => {
        void run(`Give “${r.title}” ${short(day)}`, () => setPlanned('routine', r.id, atMidnight(day), true), {
          ids: [r.id], undo: () => setPlanned('routine', r.id, atMidnight(day), false),
        })
      },
      moveRoutine: (it, fromKey, day) => {
        if (!it.routineId) return
        const opts: { ids: string[]; undo?: () => Promise<boolean | void> } = { ids: [it.routineId] }
        void run(`Move “${it.title}” to ${short(day)}`, async () => {
          const r = await moveRoutineToDay(it.routineId!, fromKey, localYmd(day), it.title, false)
          if (r.undo) { const undo = r.undo; opts.undo = async () => { await undo() } }
          return r.ok
        }, opts)
      },
      open: (id) => handleSelectBlock(id),
      // Delete from No day yet, with Undo: the same row back (restoreDeleted).
      deleteTask: (t) => {
        void activity.run(`Delete “${t.title}”`, async () => { await deleteTask(t.id); return true }, {
          ids: [t.id], undo: () => restoreDeleted(addTask, t),
        })
      },
      // A typed sentence: its date decides where it lands (a day this week, a
      // day elsewhere, or this week with no day); who and where ride the insert.
      addTyped: async (parse, landing) => {
        const id = crypto.randomUUID()
        const at = landing.kind === 'day' ? (parse.hasTime && parse.date ? parse.date : atMidnight(landing.date)) : undefined
        const people = parse.people
        const where = landing.kind === 'day' ? short(landing.date) : 'this week'
        const ok = await run(`Add “${parse.title}” to ${where}`, async () => !!(await addTask(parse.title, parse.quick.contactId, parse.quick.projectId, at, {
          id, assignedTo: people[0] ?? meId ?? undefined, assignedToAll: people.length > 1 ? people : undefined,
          location: parse.location, context: parse.quick.context,
          ...(at ? { isAllDay: !parse.hasTime } : { bucket: 'week' as const, weekStart: weekAnchor }),
        })), { ids: [id], undo: async () => { await deleteTask(id) } })
        if (ok && !layers.has(parse.quick.context ?? 'unsorted')) showToast('Added, but hidden by your current view', 'warning', 8000)
        return ok !== false
      },
      foreignDrop: (day, payload) => { void planActions.drop(payload, { type: 'day', day }).then((ok) => { if (ok === true) canvasWriteListener.current?.() }) },
      // No day yet triage: the Inbox's when-vocabulary, through the same gated
      // writes (applyTriageWhen); Undo puts back where the task was.
      triageTask: (t, when) => {
        const before = placementOf(t)
        void run(`${describeTriageWhen(when)}: “${t.title}”`, () => applyTriageWhen(when, t.id, {
          onPushTask: gated.pushTask, onSetBucket: gated.setBucket ?? ((id, bucket) => onUpdateTask(id, { bucket })),
          onFocus: (id, day) => onUpdateTask(id, { plannedOn: day }),
        }), { ids: [t.id], undo: async () => { await onUpdateTask(t.id, before) } })
      },
      // Drag to order within a day (Task 13): the shared sort_order Today's All
      // Day and the wall read, usually one row; Undo writes back every order
      // it changed, a never-ordered row back to never-ordered.
      // The orders it had are read from the day's own rows (a subtask's too).
      reorderTask: (t, dayTasks, toIndex) => {
        const orderedIds = dayTasks.map((x) => x.id)
        const was = new Map(dayTasks.map((x) => [x.id, x.sortOrder ?? null]))
        const writes = reorderTasksToIndex(orderedIds, t.id, toIndex, was)
        if (writes.length === 0) return
        const before = writes.map((w) => ({ id: w.id, sortOrder: was.get(w.id) ?? null }))
        const up = toIndex < orderedIds.indexOf(t.id)
        void activity.run(`Move “${t.title}” ${up ? 'up' : 'down'}`, () => writeOrders(writes), {
          ids: writes.map((w) => w.id), undo: async () => { await writeOrders(before) },
        })
      },
      scheduleTask: (t, at, isAllDay) => {
        const before = placementOf(t)
        void run(`Give “${t.title}” ${short(at)}`, () => gated.pushTask(t.id, isAllDay ? atMidnight(at) : at) as Promise<boolean | void>, {
          ids: [t.id], undo: async () => { await onUpdateTask(t.id, before) },
        })
      },
      // A timed event keeps its time and length on the new day; Undo puts it back.
      ...(onUpdateEvent ? {
        moveEvent: (ev: CanvasDayItem, day: Date) => {
          if (!ev.time) return
          const eventId = ev.id.replace(/^event-/, '')
          const length = ev.end ? ev.end.getTime() - ev.time.getTime() : 30 * 60_000
          const start = new Date(day.getFullYear(), day.getMonth(), day.getDate(), ev.time.getHours(), ev.time.getMinutes())
          const was = { startTime: ev.time, endTime: new Date(ev.time.getTime() + length) }
          void activity.run(`Move “${ev.title}” to ${short(day)}`,
            async () => { await onUpdateEvent(eventId, { startTime: start, endTime: new Date(start.getTime() + length) }) },
            { ids: [ev.id], undo: async () => { await onUpdateEvent(eventId, was) } })
        },
      } : {}),
    }
    // handleSelectBlock is a plain function of onSelectItem.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity, onUpdateTask, onUpdateEvent, toggleTask, writeOrders, markDone, undoDone, setPlanned, moveRoutineToDay, tasks, weekAnchor, planActions, onSelectItem, gated, addTask, deleteTask, meId, layers])
  const canvasSpans = useMemo(() => toCanvasSpans(journalSpans, journalDays), [journalSpans, journalDays])
  const canvasDayDescriptions = useMemo(() => Object.fromEntries((dayChoices ?? []).map((c) => [
    localYmd(c.date), densityDescription(c.density, c.date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })),
  ])), [dayChoices])
  // A guided session's ribbon (2026-11-04) drives the month's fold beside
  // the week: its look back turns that sheet to last week, with its choices.
  const weekCanvas = (opts?: { hideStrip?: boolean; fold?: SessionFold; ribbon?: { label: string; onPull: () => void } }) => (
    <WeekCanvas weekStart={weekAnchor} hideStrip={opts?.hideStrip} days={canvasDays.days} shelf={shelf} routinesToPlace={routinesToPlace}
      rhythm={canvasDays.lines} isCurrent={weekIsCurrentFor(weekAnchor)} writers={canvasWriters}
      spans={canvasSpans} dayDescriptions={canvasDayDescriptions} members={familyMembers} viewerId={meId ?? undefined}
      pointing={activity.pointing} fold={opts?.fold} ribbon={opts?.ribbon}
      onPlaceUnplaced={deckAvailable ? openPlace : undefined} unplacedCount={placeCount} month={monthSheet} />
  )
  const todayLine = null

  // A past week is a look-back, not a plan.
  const weekIsPast = weekAnchor.getTime() + 7 * 86_400_000 <= Date.now()

  // ── The deck (Friday session, Stage 1) ────────────────────────────────
  // Review and Plan (?review=1, ?plan=1) open the full deck; "Place them"
  // (?place=1) only this week's cards. Everything it deals, counts and drains
  // comes from the page's own scoped list: the layers (domain filter) and the
  // people lens, never the unfiltered tasks — a private Work task never
  // reaches a Family view, as a card or as a count. With nobody chosen, the
  // list is what the Week list shows the viewer (weekListTasks' doableBy):
  // unassigned or theirs, never a task only someone else carries.
  // Only on this week, or next week on this week's last day; elsewhere the
  // deck's URL is ignored and the week shows itself.
  const [params, setParams] = useSearchParams()
  const deckAvailable = deckWeekAllowed(weekAnchor, new Date(), readCadenceConfig().weekStartsOn)
  const deckMode = deckAvailable ? deckModeOf(params) : null
  const tasksLoaded = (sources?.tasks ?? 'ready') === 'ready'
  const lastWeekStart = useMemo(() => new Date(weekAnchor.getFullYear(), weekAnchor.getMonth(), weekAnchor.getDate() - 7), [weekAnchor])
  const nextWeekStart = useMemo(() => new Date(weekAnchor.getFullYear(), weekAnchor.getMonth(), weekAnchor.getDate() + 7), [weekAnchor])
  // Nothing is left out for being marked a goal (paper-style horizons,
  // 2026-11-04): the deck, last week's counts and No day yet read one rule.
  const scopedTasks = useMemo(() => layerTasks.filter(lens.keep), [layerTasks, lens])
  const deckTasks = useMemo(() => scopedTasks.filter((t) => lens.on || !meId || doableBy(t, meId)), [scopedTasks, lens, meId])
  // The drains never move a task assigned only to someone else, even where
  // the lens shows it (Stage 2's household session decides those together).
  // Not knowing who is looking, only unassigned tasks move.
  const drainable = useCallback((t: Task) => doableBy(t, meId ?? ''), [meId])
  const deckActions = useDeckActions({ toggleTask, updateTask, onUpdateTask, keepForward, gated, weekAnchor, lastWeekStart, nextWeekStart })
  // What each card serves, one level up: "For October · {goal title}".
  const servesOf = useCallback((t: Task) => {
    const parentId = t.goalTaskId ?? t.sourceId
    // Looked up within the layers too: a goal the view hides is never named.
    const parent = parentId ? layerTasks.find((p) => p.id === parentId) : undefined
    if (!parent || parent.bucket !== 'month') return undefined
    const month = (parent.monthStart ?? monthStartOf(new Date())).toLocaleDateString('en-US', { month: 'long' })
    return `For ${month} · ${parent.title}`
  }, [layerTasks])
  const deckCards = useMemo(() => buildDeck({
    tasks: deckTasks, weekStart: weekAnchor, lastWeekStart, isCurrent: weekIsCurrent, servesOf,
    only: deckMode === 'place' ? 'this-week' : undefined,
  }), [deckTasks, weekAnchor, lastWeekStart, weekIsCurrent, servesOf, deckMode])
  // "{n} without a day" counts exactly what "Place them" deals.
  const placeCount = useMemo(() => buildDeck({
    tasks: deckTasks, weekStart: weekAnchor, lastWeekStart, isCurrent: weekIsCurrent, only: 'this-week',
  }).length, [deckTasks, weekAnchor, lastWeekStart, weekIsCurrent])
  const doneLastWeek = useMemo(
    () => deckTasks.filter((t) => weekReviewState(t, lastWeekStart, { isCurrent: false }) === 'done').length,
    [deckTasks, lastWeekStart],
  )
  const plannedLastWeek = useMemo(
    () => deckTasks.filter((t) => weekReviewState(t, lastWeekStart, { isCurrent: false }) !== undefined).length,
    [deckTasks, lastWeekStart],
  )
  // This week's count for the header: done, out of everything the page draws
  // on it (weekShown). The guided session's "{n} of about 15" is onThisWeek.
  // A week's done is what was done in it: a row finished before the week
  // began, or let go, is not on the count.
  const countedThisWeek = useMemo(
    // An undated legacy completion (no completed_at, day, stamp or
    // commitment) is on no week (isUndatedLegacyDone).
    () => weekShown.filter((t) => !t.dismissedAt && !isUndatedLegacyDone(t) && !(t.completed && t.completedAt && t.completedAt < weekAnchor)),
    [weekShown, weekAnchor],
  )
  const doneThisWeek = useMemo(
    () => countedThisWeek.filter((t) => weekReviewState(t, weekAnchor, { isCurrent: weekIsCurrent }) === 'done').length,
    [countedThisWeek, weekAnchor, weekIsCurrent],
  )
  const onThisWeek = countedThisWeek.length
  const alsoDoneLastWeek = useMemo(
    () => alsoDoneIn(deckTasks, lastWeekStart, weekAnchor, (t) => weekReviewState(t, lastWeekStart, { isCurrent: false }) !== undefined),
    [deckTasks, lastWeekStart, weekAnchor],
  )
  // The page's own way into the deck ("Place them"), the same URLs WeekV2's
  // buttons set, on the week being viewed. The deck on a week reviews the
  // week before it, which is the week the header's fact line counts. The one
  // record of that review: WeekV2 reads and finishes it through this same
  // instance (deck.lastReview), so the line says "reviewed {Weekday}" the
  // moment the deck closes.
  const lastReview = usePeriodReview('weekly', weekToken(lastWeekStart))
  const openDeck = (mode: 'place') => setParams((prev) => {
    const p = new URLSearchParams(prev)
    p.delete('review'); p.delete('plan'); p.delete('place')
    p.set(mode, '1')
    return p
  })
  const openPlace = () => openDeck('place')
  // The drain runs as the full deck opens, once per mount (the ref), and only
  // once the tasks are in. It is idempotent across mounts: what it moves
  // leaves the Inbox and the week, so the next opening finds nothing.
  const [aged, setAged] = useState(0)
  const drained = useRef(false)
  useEffect(() => {
    if (deckMode !== 'full' || !tasksLoaded || drained.current || !planV2Enabled()) return
    drained.current = true
    const stale = staleForDrain(deckTasks, new Date()).filter(drainable)
    setAged(stale.length)
    if (stale.length > 0) void deckActions.drain(stale)
  }, [deckMode, tasksLoaded, deckTasks, drainable, deckActions])
  // The strip inside a card's "This week": the week's seven days from its own
  // start (weekStartsOn), each with what is already there — the day's timed
  // events and tasks by time, three at most, the rest counted. A day already
  // gone is shown but cannot be given.
  const stripDays = useMemo<StripDay[]>(() => Array.from({ length: 7 }, (_, i) => {
    const now = new Date()
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const date = new Date(weekAnchor.getFullYear(), weekAnchor.getMonth(), weekAnchor.getDate() + i)
    const key = localYmd(date)
    const endOfDay = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1).getTime()
    const items: { at: number; title: string }[] = []
    for (const ev of events) {
      if (isAllDayEvent(ev)) continue
      const startStr = (ev as { start_time?: string }).start_time ?? (ev as { startTime?: string }).startTime
      const start = startStr ? new Date(startStr) : null
      if (start && !Number.isNaN(start.getTime()) && localYmd(start) === key) items.push({ at: start.getTime(), title: ev.title })
    }
    for (const t of scopedTasks) {
      if (t.completed || !t.scheduledFor || localYmd(t.scheduledFor) !== key) continue
      // A task with no time sits after the day's timed things.
      items.push({ at: t.isAllDay === false ? t.scheduledFor.getTime() : endOfDay, title: t.title })
    }
    items.sort((a, b) => a.at - b.at)
    return {
      key, date,
      label: `${date.toLocaleDateString('en-US', { weekday: 'short' })} ${date.getDate()}`,
      isWeekend: date.getDay() === 0 || date.getDay() === 6,
      past: date < todayStart,
      lines: items.slice(0, 3).map((it) => it.title),
      more: Math.max(0, items.length - 3),
    }
  }), [weekAnchor, events, scopedTasks])
  // ── Plan this week (spec 2026-10-19): Choose from the month, then days ──
  // October's lines from the same scoped rows (areas and the people lens),
  // the month this week mostly lives in.
  const planPool = useMemo(() => {
    const ids = new Set(deckTasks.map((t) => t.id))
    return [...deckTasks, ...planLines.filter((t) => !ids.has(t.id))]
  }, [deckTasks, planLines])
  const chooseWeek = useChooseWeek({
    lines: planLines, pool: planPool, weekAnchor, lastWeekStart, monthStart: planMonth, isCurrent: weekIsCurrent,
    actions: deckActions,
  })
  // The guided session's calendar check reads the timed events the days draw
  // (the page's space filter); Look back hands a loose end to anyone else.
  const clashEvents = useMemo(() => eventItems.flatMap((i) => i.startTime && i.endTime
    ? [{ id: i.id, title: i.title, start: i.startTime, end: i.endTime, ...(i.location ? { place: i.location } : {}) }] : []), [eventItems])
  // And the all-day ones (a trip, a holiday, a school break), each on every
  // day it covers: walking each day, a day with a trip on it is not
  // "Nothing fixed" (walk 2026-11-02). They are not clash-checked: an
  // all-day event has no hours to overlap.
  const allDayEvents = useMemo(() => {
    const first = localYmd(weekAnchor), last = localYmd(new Date(weekAnchor.getFullYear(), weekAnchor.getMonth(), weekAnchor.getDate() + 6))
    return events.flatMap((ev) => {
      if (!isAllDayEvent(ev)) return []
      const span = eventDays(ev)
      if (!span || span.last < first || span.first > last) return []
      return [{ id: `event-${ev.google_event_id || ev.id}`, title: ev.title.trim(), first: span.first, last: span.last }]
    })
  }, [events, weekAnchor])
  // Weekly ops lists the routines the page shows: the same space filter and
  // people lens (the ladder without its one-day rung; the step asks each day).
  const opsRoutines = useMemo(() => routines.filter((r) => resolveRoutineEligible(r, { member: selectedAssignees, prefs: { hideRoutines: false, layers } }).shows),
    [routines, selectedAssignees, layers])
  const deck: WeekDeck = {
    mode: deckMode, available: deckAvailable,
    openTask: (id) => onSelectItem(`task-${id}`),
    cards: deckCards, doneLastWeek, plannedLastWeek, alsoDoneLastWeek, doneThisWeek, onThisWeek, aged, actions: deckActions, stripDays,
    loading: !tasksLoaded, lastReview,
    onSeeAged: () => navigate('/someday'),
    choose: chooseWeek,
    tasks: deckTasks,
    events: clashEvents,
    allDayEvents,
    routines: opsRoutines,
    listenCanvasWrites,
  }

  return (
    <WeekPlanHost tasks={tasks} weekStart={weekAnchor} meId={meId} isPast={weekIsPast}>
    <div className="relative week-content">

      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={handleDragStart}
        onDragEnd={drag.dndHandlers.onDragEnd}
        onDragCancel={drag.dndHandlers.onDragCancel}
        onDragMove={handleDragMove}
      >
        {/* The week list stands LEFT of the days, the month list folded beneath
            it (Scott, 2026-09-05: chips down the side, not along the top).
            Inside the DndContext so the rows' useDraggable registers; drops
            ride the existing chip branches in useWeekDragDrop. The month is
            the rung above, read-only — the week is planned by looking at it,
            never by dragging from it. On a narrow screen the list sits above
            the stacked days instead. */}
        {narrow && planV2Enabled() ? (
          <WeekV2 weekStart={weekAnchor} isCurrent={weekIsCurrent} canvas={weekCanvas} today={todayLine} deck={deck} />
        ) : narrow ? (
          <div className="flex flex-col gap-4">
            {weekListFor()}
            <h2 className="week-days-heading">The days</h2>
            <WeekJournal days={journalDays} weekend={journalWeekend} spans={journalSpans} onSelectItem={handleSelectBlock} onToggleEntry={handleJournalToggle} onPlanDrop={handlePlanDropOnDay} onAddToDay={handleAddToDay} narrow dragEnabled={false} timingControl={weekTimingControl} forecast={forecast} />
          </div>
        ) : (
        <div className="flex items-start gap-4">
        {/* Edge auto-advance measures THIS box, not the whole view. */}
        <div ref={gridBoundsRef} data-week-bounds className="flex-1 min-w-0">
        {planV2Enabled() ? (
          // v2 (docs/planning/2026-09-28-planning-v2.md): the same journal and
          // list, laid out as the prototype's day column + "Any day this week".
          <WeekV2 weekStart={weekAnchor} isCurrent={weekIsCurrent} canvas={weekCanvas} today={todayLine} deck={deck} />
        ) : (
          <>
            {weekListFor()}
            <h2 className="week-days-heading">The days</h2>
            <WeekJournal days={journalDays} weekend={journalWeekend} spans={journalSpans} onSelectItem={handleSelectBlock} onToggleEntry={handleJournalToggle} onPlanDrop={handlePlanDropOnDay} onAddToDay={handleAddToDay} timingControl={weekTimingControl} forecast={forecast} />
          </>
        )}
        </div>
        </div>
        )}

        <DragOverlay dropAnimation={null}>
          {drag.activeDragId
            ? (() => {
                // All-day strip chips drag with a 'chip:<taskId>' id and aren't
                // in placedItems — render their own floating pill so the drag
                // has visible feedback (without this the chip looked unmovable).
                // List pills ('pool:' tasks, 'poolroutine:' routines). Without
                // this the drag had NO visible feedback at all — the pill
                // stayed put (a list pill applies no transform, by design:
                // the list must not reflow mid-drag) and the DragOverlay
                // rendered null, so a drag that was working perfectly well
                // looked like a pill that could not be picked up.
                if (drag.activeDragId.startsWith('pool:')) {
                  const task = tasks.find((t) => t.id === drag.activeDragId!.slice('pool:'.length))
                  if (!task) return null
                  return (
                    <div className="pointer-events-none max-w-[260px] rounded-lg border border-primary-300 bg-white px-2 py-1.5 text-[13px] text-neutral-800 shadow-lg">
                      {task.title}
                    </div>
                  )
                }
                // One drag rule (2026-10-03): a month line ('ref:<taskId>') and a
                // routine occurrence ('occ:<routineId>:<day>') carry their pill too.
                if (drag.activeDragId.startsWith('ref:')) {
                  const task = tasks.find((t) => t.id === drag.activeDragId!.slice('ref:'.length))
                  if (!task) return null
                  return (
                    <div className="pointer-events-none max-w-[260px] rounded-lg border border-primary-300 bg-white px-2 py-1.5 text-[13px] text-neutral-800 shadow-lg">
                      {task.title}
                    </div>
                  )
                }
                if (drag.activeDragId.startsWith('poolroutine:') || drag.activeDragId.startsWith('occ:')) {
                  const id = drag.activeDragId.startsWith('occ:') ? drag.activeDragId.split(':')[1] : drag.activeDragId.slice('poolroutine:'.length)
                  const routine = routines.find((r) => r.id === id)
                  if (!routine) return null
                  return (
                    <div className="pointer-events-none max-w-[260px] rounded-lg border border-primary-200 bg-primary-50 px-2 py-1.5 text-[12.5px] font-semibold text-primary-800 shadow-lg">
                      {routine.name}
                    </div>
                  )
                }
                if (drag.activeDragId.startsWith('chip:') || drag.activeDragId.startsWith('journal:')) {
                  const taskId = drag.activeDragId.slice(drag.activeDragId.indexOf(':') + 1)
                  const task = tasks.find((t) => t.id === taskId)
                  if (!task) return null
                  return (
                    <div className="opacity-80 pointer-events-none">
                      <div className="px-3 py-1.5 rounded-full bg-bg-elevated border border-neutral-300 text-[12px] text-neutral-800 shadow-md whitespace-nowrap">
                        {task.title}
                      </div>
                    </div>
                  )
                }
                return null
              })()
            : null}
        </DragOverlay>
      </DndContext>
    </div>
    </WeekPlanHost>
  )
}

/** Calendar events as items, with the day's Specials folded into its School
 *  block subtitle (or kept as their own item on a day without one). */
function weekEventItems(extras: ReturnType<typeof partitionWeekExtras>) {
  const items = extras.rest.map(eventToTimelineItem)
  const schoolByDay = new Map<string, (typeof items)[number]>()
  for (const item of items) {
    if (item.startTime && /^school\b/i.test(item.title)) {
      const key = localYmd(item.startTime)
      if (!schoolByDay.has(key)) schoolByDay.set(key, item)
    }
  }
  for (const [key, entries] of extras.specialsByDay) {
    const school = schoolByDay.get(key)
    if (school) {
      school.subtitle = entries.map((e) => e.label).join(' · ')
    } else {
      items.push(...entries.map((e) => eventToTimelineItem(e.event)))
    }
  }
  return items
}

/** The week's own list on the canvas: what is chosen for the week (`listed`,
 *  through the people lens) less what waits for its weekend, flat. */
function weekShelf(anchor: Date, listed: Task[]) {
  const end = anchor.getTime() + 7 * 86_400_000
  const inWeekend = (t: Task) => !!t.weekendStart && !t.scheduledFor && t.weekendStart.getTime() >= anchor.getTime() && t.weekendStart.getTime() < end
  return buildShelf({ weekTasks: listed.filter((t) => !inWeekend(t)), weekStart: anchor })
}

/** The days as the canvas draws them, from the days buildJournalDays assembles. */
function toCanvasDays({ days: journalDays, weekend: journalWeekend, shelf, dailyShown, forecast, plannedDinners, eventsReady, canMoveEvent }: {
  days: JournalDay[]
  weekend: JournalWeekend | null
  shelf: ShelfGroup[]
  dailyShown: boolean
  forecast: Record<string, DayForecast>
  plannedDinners: Map<string, PlannedMeal>
  /** The calendar read covers the week: open time may be counted. */
  eventsReady: boolean
  /** A timed event that may be dragged to another day, by its id. */
  canMoveEvent: (id: string) => boolean
}) {
  const onShelf = new Set(shelf.flatMap((g) => g.rows.map((r) => r.task.id)))
  // Repeated tasks too ("Pick up from FFG" each weekday), and repeats on
  // part of the week ("Mon–Thu"): written once above.
  const found = weekRhythm(journalDays, undefined, { tasks: true, partial: true })
  const rhythm = dailyShown ? { everyDay: [], weekdays: [], repeats: [], days: journalDays } : found
  const toItem = (e: JournalEntry): CanvasDayItem => ({
    id: e.id, kind: e.kind, title: e.title, subtitle: e.subtitle, time: e.time, completed: e.completed, task: e.task, routineId: e.routineId, people: e.people,
    ...(e.kind === 'event' ? { end: e.end, movable: !!e.time && canMoveEvent(e.id) } : {}),
  })
  // Open calendar time is planning information: today and the days ahead,
  // counted from the whole day — every-day routines too, though written above.
  const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0)
  const whole = new Map(journalDays.map((d) => [d.key, d]))
  const days: CanvasDay[] = rhythm.days.map((d, i) => {
    const events = [
      ...d.notes.map((ev): CanvasDayItem => ({ id: `event-${ev.google_event_id || ev.id}`, kind: 'event', title: ev.title, completed: false })),
      ...d.entries.filter((e) => e.kind === 'event').map(toItem),
    ]
    // "Specials — Ella: Library · Kaleb: Art" is one chip per kid, as on
    // Today (ROW-LANGUAGE: specials), not an event row.
    const specials = events.flatMap((ev) => parseSpecials(ev.title) ?? [])
    return {
      date: d.date, key: d.key,
      events: events.filter((ev) => !parseSpecials(ev.title)),
      specials,
      // The day's order (Task 13): tasks by the order dragged into them, then
      // time, then title; the top two are what the day shows.
      items: [...inDayOrder(d.entries), ...d.foldedRoutines].filter((e) => !(e.task && onShelf.has(e.task.id))).map(toItem),
      sometime: journalWeekend && journalWeekend.satIndex === i ? journalWeekend.sometime.map(toItem) : undefined,
      dinners: d.dinners.length
        ? d.dinners.map(({ event, label }) => ({ id: `event-${event.google_event_id || event.id}`, label }))
        : plannedDinners.has(d.key)
          ? [{ id: `meal-${d.key}`, label: plannedDinners.get(d.key)!.title, href: plannedMealHref(plannedDinners.get(d.key)!) }]
          : [],
      weather: forecast[d.key] ? <DayWeather weather={forecast[d.key]} narrow={false} /> : undefined,
      // The hours with nothing scheduled ("Calendar clear 10:30a–4:15p"),
      // not a promise of spare time: untimed tasks still want some.
      free: d.date >= todayStart && eventsReady
        ? formatFree(dayFreeWindows(whole.get(d.key)?.entries ?? d.entries)) : undefined,
    }
  })
  const lines = rhythmLines(rhythm)
  return { days, lines }
}

/** A stay across days, written once: one bar across the columns it covers. */
function toCanvasSpans(journalSpans: ReturnType<typeof layoutContextSpans>, journalDays: JournalDay[]) {
  const ymdLabel = (ymd: string) => { const [y, m, d] = ymd.split('-').map(Number); const at = new Date(y, m - 1, d); return `${at.toLocaleDateString('en-US', { weekday: 'short' })} ${at.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` }
  const wd = (i: number) => journalDays[i].date.toLocaleDateString('en-US', { weekday: 'short' })
  return journalSpans.map((sp) => ({
    id: `event-${sp.event.google_event_id || sp.event.id}`,
    when: `${wd(sp.startCol)}${sp.endCol !== sp.startCol ? `–${wd(sp.endCol)}` : ''}`,
    from: journalDays[sp.startCol].key, to: journalDays[sp.endCol].key,
    title: sp.event.title.trim(),
    tail: sp.continuesBefore || sp.continuesAfter ? `${sp.continuesBefore ? `, from ${ymdLabel(sp.first)}` : ''}${sp.continuesAfter ? `, through ${ymdLabel(sp.last)}` : ''}` : undefined,
  }))
}

